import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrafficIcon from "@mui/icons-material/Traffic";
import { Box, CircularProgress, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import StatBox from "../../components/StatBox";
import { tokens } from "../../theme";

import { API_BASE, authFetch } from "../../Utils/apiClient";

const formatMoney = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch("/admin/dashboard-stats");
        const json = await res.json();
        if (json.status) setStats(json.result);
        else setError(json.message || "Could not load stats");
      } catch (err) {
        console.error("Dashboard stats failed:", err);
        setError("Failed to connect to API");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  const s = stats || {};

  return (
    <Box m="20px" height="70vh" overflow="auto" paddingRight="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Dashboard" subtitle="Live stats from JCGN platform" />
      </Box>
      {error && (
        <Box mb={2} color="error.main">
          {error}
        </Box>
      )}

      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="140px"
        gap="20px"
      >
        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} display="flex" alignItems="center" justifyContent="center">
          <StatBox
            title={String(s.usersCount ?? 0)}
            subtitle="Total Users"
            progress="0.80"
            increase=""
            icon={<TrafficIcon sx={{ color: colors.greenAccent[600], fontSize: "26px" }} />}
          />
        </Box>
        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} display="flex" alignItems="center" justifyContent="center">
          <StatBox
            title={String(s.paidOrdersCount ?? 0)}
            subtitle="Paid Orders"
            progress="0.50"
            increase=""
            icon={<ShoppingCartIcon sx={{ color: colors.greenAccent[600], fontSize: "26px" }} />}
          />
        </Box>
        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} display="flex" alignItems="center" justifyContent="center">
          <StatBox
            title={formatMoney(s.salesTotal)}
            subtitle="Shop Sales Total"
            progress="0.50"
            increase=""
            icon={<AttachMoneyIcon sx={{ color: colors.greenAccent[600], fontSize: "26px" }} />}
          />
        </Box>
        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} display="flex" alignItems="center" justifyContent="center">
          <StatBox
            title={String(s.processingOrdersCount ?? 0)}
            subtitle="Orders Processing"
            progress="0.30"
            increase=""
            icon={<ReceiptIcon sx={{ color: colors.greenAccent[600], fontSize: "26px" }} />}
          />
        </Box>

        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} display="flex" alignItems="center" justifyContent="center">
          <StatBox
            title={String(s.donationsCount ?? 0)}
            subtitle="Donations Received"
            progress="0.20"
            increase=""
            icon={<ReceiptIcon sx={{ color: colors.greenAccent[600], fontSize: "26px" }} />}
          />
        </Box>
        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} display="flex" alignItems="center" justifyContent="center">
          <StatBox
            title={formatMoney(s.donationsTotal)}
            subtitle="Donations Total"
            progress="0.20"
            increase=""
            icon={<AttachMoneyIcon sx={{ color: colors.greenAccent[600], fontSize: "26px" }} />}
          />
        </Box>
        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} display="flex" alignItems="center" justifyContent="center">
          <StatBox
            title={String(s.subscribersCount ?? 0)}
            subtitle="Active Subscribers"
            progress="0.25"
            increase=""
            icon={<PersonAddIcon sx={{ color: colors.greenAccent[600], fontSize: "26px" }} />}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
